import { useEffect } from "react";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { useCart } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { normaliseImageUrl, handleImageError } from "../lib/driveImage";

export default function CartSheet({ open, onClose }) {
  const { items, remove, setQty, subtotal, shippingTotal, total, count } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 bg-foreground/40 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid="cart-sheet"
        className={`fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-background border-l border-border shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between px-6 h-20 border-b border-border">
          <h2 className="font-serif text-2xl">Your Cart <span className="text-sm text-muted-foreground font-price font-medium">({count})</span></h2>
          <button data-testid="close-cart-btn" onClick={onClose} className="p-2 hover:text-terracotta"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" style={{ maxHeight: "calc(100vh - 220px)" }}>
          {items.length === 0 ? (
            <div className="text-center text-muted-foreground py-16">Your cart is empty.</div>
          ) : items.map((it) => (
            <div key={it.key} data-testid={`cart-item-${it.key}`} className="flex gap-3 border-b border-border pb-4">
              <img
                src={normaliseImageUrl(it.image)}
                alt={it.name}
                className="w-20 h-24 object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => handleImageError(e, it.image)}
              />
              <div className="flex-1">
                <div className="text-sm font-medium leading-snug">{it.name}</div>
                {it.variantTag && it.variantTag !== "Default" && <div className="text-xs text-muted-foreground mt-0.5">Variant: {it.variantTag}</div>}
                <div className="text-sm font-price font-bold text-foreground mt-1">₹{it.price}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`cart-dec-${it.key}`} onClick={() => setQty(it.key, it.quantity - 1)} className="w-7 h-7 border border-border flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                  <span data-testid={`cart-qty-${it.key}`} className="text-sm w-8 text-center font-price font-semibold">{it.quantity}</span>
                  <button data-testid={`cart-inc-${it.key}`} onClick={() => setQty(it.key, it.quantity + 1)} className="w-7 h-7 border border-border flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  <button data-testid={`cart-remove-${it.key}`} onClick={() => remove(it.key)} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="absolute bottom-0 left-0 right-0 border-t border-border p-6 bg-background">
          <div className="space-y-1.5 mb-4 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span>
              <span className="font-price font-semibold text-foreground">₹{subtotal}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Shipping Charge</span>
              <span>{shippingTotal > 0 ? <span className="font-price font-semibold text-foreground">₹{shippingTotal}</span> : <span className="text-emerald-700 font-semibold">FREE</span>}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-border font-medium">
              <span>Total</span>
              <span data-testid="cart-total" className="font-price text-2xl font-bold text-foreground">₹{total}</span>
            </div>
          </div>
          <button
            data-testid="checkout-btn"
            disabled={items.length === 0}
            onClick={() => { onClose(); nav("/checkout"); }}
            className="w-full py-3 rounded-full bg-terracotta text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Checkout
          </button>
        </div>
      </aside>
    </>
  );
}
